/**
 * `src/lib/ui/reader/device.ts` — the install's own id, minted once.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  THIS BELONGS IN `src/lib/db/`, AND IT IS HERE BECAUSE M2 IS THE FIRST
 *  CONSUMER. MOVE IT WHEN THE SECOND ONE ARRIVES.
 * ═══════════════════════════════════════════════════════════════════════════
 * `schema.ts` declares `MetaRow.key` including `'device_id'` — "UUIDv7, minted
 * once per install, never rotated" — and `ids.ts` declares `positionId(surface,
 * deviceId)`. Nothing mints it: M0 froze the shape and M1 built the pipeline,
 * so the first code that actually needs a device id is the reader's position
 * row. It is written here rather than in `src/lib/db/queries.ts` only because
 * that file does not exist yet and inventing it for fourteen lines would be a
 * worse guess about M4's shape than leaving it where its one caller is.
 *
 * When M4 (backup, `from_device_label` on conflicts) or M8 (`audio.output_
 * device`) needs it, move both functions verbatim into `src/lib/db/queries.ts`
 * and re-export. Nothing depends on their location: the id lives in `meta`,
 * which is frozen, device-local, and never exported.
 *
 * THE READ-MODIFY-WRITE IS IN A TRANSACTION FOR A REASON. Two tabs opening the
 * app for the first time at the same moment both read `undefined`, both mint,
 * and both write — and the loser's `positions` row is stranded under an id
 * nothing will ever look up again. Dexie's `'rw'` transaction over `meta` makes
 * that a serialisation problem instead of a race.
 */

import { uuidv7 } from '$lib/db/ids';
import { db } from '$lib/db/schema';

/** Who this install is, as far as device-local rows are concerned. */
export interface DeviceIdentity {
	/** UUIDv7. */
	id: string;
	/** `meta.device_label`, or `''` until the user names this device (M4). */
	label: string;
	/**
	 * `false` when IndexedDB could not be reached and this id exists only for
	 * the lifetime of the tab.
	 *
	 * SPEC §1.1: "IndexedDB is a cache." A private window, an evicted origin or
	 * a corrupt profile must not stop the mushaf from opening — so the reader
	 * still gets an id, still tracks where it is, and simply cannot persist it.
	 * The caller is told, so nothing claims a position was saved when it was not.
	 */
	persisted: boolean;
}

let cached: DeviceIdentity | null = null;
let inFlight: Promise<DeviceIdentity> | null = null;

/** The install's device identity, minted on first call and memoised. */
export function deviceIdentity(): Promise<DeviceIdentity> {
	if (cached) return Promise.resolve(cached);
	inFlight ??= resolveIdentity()
		.then((identity) => {
			cached = identity;
			inFlight = null;
			return identity;
		})
		.catch((error: unknown) => {
			inFlight = null;
			throw error;
		});
	return inFlight;
}

async function resolveIdentity(): Promise<DeviceIdentity> {
	try {
		return await db.transaction('rw', db.meta, async () => {
			const existing = await db.meta.get('device_id');
			const stored = existing?.value;
			if (typeof stored === 'string' && stored !== '') {
				const labelRow = await db.meta.get('device_label');
				const label = typeof labelRow?.value === 'string' ? labelRow.value : '';
				return { id: stored, label, persisted: true };
			}
			const minted = uuidv7();
			await db.meta.put({ key: 'device_id', value: minted });
			return { id: minted, label: '', persisted: true };
		});
	} catch {
		return { id: uuidv7(), label: '', persisted: false };
	}
}

/** Drop the memo. Tests only; the app mints once per document. */
export function resetDeviceIdentity(): void {
	cached = null;
	inFlight = null;
}
